// Trade anlegen / bearbeiten / abrechnen – Felder wie in der bisherigen JournalPage
import { ReactNode, useEffect, useState } from "react";
import { ASSET_CLASSES, BROKERS, CATEGORIES, CURRENCIES, SIGMUND_STRATEGIES, api, clampDate } from "./common";

export function Sheet({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="sheet-wrap" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="sheet-head">
          <span>{title}</span>
          <button className="nx-icon-btn" aria-label="Schließen" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

export const Field = ({ label, full, children }: { label: string; full?: boolean; children: ReactNode }) => (
  <label className={`jf-field${full ? " full" : ""}`}><span>{label}</span>{children}</label>
);

function StrategiesInput({ strategies, onChange }: { strategies: string[]; onChange: (s: string[]) => void }) {
  const update = (i: number, v: string) => { const s = [...strategies]; s[i] = v; onChange(s); };
  return (
    <div className="jf-stack">
      {strategies.map((s, i) => (
        <div key={i} className="jf-inline">
          <select value={s} onChange={e => update(i, e.target.value)}>
            <option value="">Strategie {i + 1} wählen</option>
            {SIGMUND_STRATEGIES.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.items.map(it => <option key={it.value} value={it.value}>{it.label}</option>)}
              </optgroup>
            ))}
          </select>
          {strategies.length > 1 && <button type="button" className="jf-mini" aria-label="Strategie entfernen"
            onClick={() => { const n = strategies.filter((_, k) => k !== i); onChange(n.length ? n : [""]); }}>−</button>}
          {i === strategies.length - 1 && strategies.length < 4 && s !== "" &&
            <button type="button" className="jf-mini" aria-label="weitere Strategie" onClick={() => onChange([...strategies, ""])}>+</button>}
        </div>
      ))}
    </div>
  );
}

export function TradeFields({ form, f }: { form: any; f: (k: string, v: any) => void }) {
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [fetching, setFetching] = useState(false);
  const isOS = form.asset_class === "options_warrant", isKO = form.asset_class === "knock_out";
  const search = async (q: string) => {
    if (!q || q.length < 2) return;
    setSearching(true);
    try { const r = await api.searchSymbol(q); setResults(r.results || []); } catch { setResults([]); }
    setSearching(false);
  };
  const fetchUnderlying = async () => {
    if (!form.underlying_symbol || !form.opened_at) return;
    setFetching(true);
    const p = await api.priceAt(form.underlying_symbol, form.opened_at);
    if (p) f("underlying_price_at_entry", Number(p).toFixed(2));
    setFetching(false);
  };
  const inp = (k: string, props: any = {}) => <input value={form[k] ?? ""} onChange={e => f(k, e.target.value)} {...props} />;
  const dt = (k: string) => <input type="datetime-local" value={form[k] ?? ""} onChange={e => f(k, clampDate(e.target.value))} />;

  return (
    <div className="jf-grid">
      <Field label="Symbol oder Name suchen" full>
        <div className="jf-inline">
          <input value={form.symbol} placeholder="z. B. AAPL, RHM.DE, BTC-USD"
            onChange={e => f("symbol", e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); search(form.symbol); } }} />
          <button type="button" className="jf-btn" onClick={() => search(form.symbol)}>{searching ? "…" : "Suchen"}</button>
        </div>
        {results.length > 0 && (
          <div className="jf-results">
            {results.map((h: any) => (
              <button type="button" key={h.symbol} onClick={() => { f("symbol", h.symbol); f("name", h.name || ""); setResults([]); }}>
                <span className="mono">{h.symbol}</span><span>{h.name}</span><span className="mono">{h.price ?? ""}</span>
              </button>
            ))}
          </div>
        )}
        {form.name && <span className="jf-note">{form.name}</span>}
      </Field>
      <Field label="ISIN (optional)">{inp("isin")}</Field>
      <Field label="Produkt">
        <select value={form.asset_class} onChange={e => f("asset_class", e.target.value)}>
          {ASSET_CLASSES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </Field>
      {isOS && <Field label="Call / Put"><select value={form.call_put || ""} onChange={e => f("call_put", e.target.value)}>
        <option value="">wählen</option><option value="call">Call</option><option value="put">Put</option></select></Field>}
      {(isKO || (!isOS && !isKO)) && <Field label="Richtung"><select value={form.direction || ""} onChange={e => f("direction", e.target.value)}>
        <option value="">–</option><option value="long">Long</option><option value="short">Short</option></select></Field>}
      {(isOS || isKO) && (
        <Field label="Underlying" full>
          <div className="jf-inline">
            <input value={form.underlying_symbol || ""} placeholder="z. B. RHM.DE" onChange={e => f("underlying_symbol", e.target.value.toUpperCase())} />
            <button type="button" className="jf-btn" onClick={fetchUnderlying} disabled={fetching || !form.underlying_symbol || !form.opened_at}>
              {fetching ? "…" : "Kurs holen"}</button>
            {form.underlying_price_at_entry && <span className="mono jf-note">{form.underlying_price_at_entry}</span>}
          </div>
        </Field>
      )}
      <Field label="Kategorie"><select value={form.category || ""} onChange={e => f("category", e.target.value)}>
        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
      <Field label="Währung"><select value={form.currency} onChange={e => f("currency", e.target.value)}>
        {CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></Field>

      <label className="jf-check full">
        <input type="checkbox" checked={!!form.is_sparplan} onChange={e => f("is_sparplan", e.target.checked)} />
        <span>Mehrere Tranchen (Sparplan)</span>
      </label>
      {form.is_sparplan && <span className="jf-note full">Tranchen trägst du nach dem Speichern in den Details ein. Ø-Einstieg und P&L rechnet der Server.</span>}

      {!form.is_sparplan && <>
        <Field label="Einstieg">{inp("entry_price", { type: "number", inputMode: "decimal", step: "any" })}</Field>
        <Field label="Stückzahl">{inp("quantity", { type: "number", inputMode: "decimal", step: "any" })}</Field>
        <Field label="Ausstieg (leer = offen)">{inp("exit_price", { type: "number", inputMode: "decimal", step: "any" })}</Field>
        <Field label="Aktueller Kurs (optional)">{inp("current_price", { type: "number", inputMode: "decimal", step: "any" })}</Field>
        <Field label="Eröffnet">{dt("opened_at")}</Field>
        <Field label="Geschlossen (leer = offen)">{dt("closed_at")}</Field>
      </>}
      {form.is_sparplan && <>
        <Field label="Eröffnet (erste Tranche)">{dt("opened_at")}</Field>
        <Field label="Aktueller Kurs (optional)">{inp("current_price", { type: "number", inputMode: "decimal", step: "any" })}</Field>
      </>}
      <Field label={form.is_sparplan ? "Kaufkosten (aus Tranchen)" : "Gebühren Kauf (€)"}>{inp("fees_entry", { type: "number", step: "any", readOnly: form.is_sparplan })}</Field>
      <Field label={form.is_sparplan ? "Verkaufskosten (aus Tranchen)" : "Gebühren Verkauf (€)"}>{inp("fees_exit", { type: "number", step: "any", readOnly: form.is_sparplan })}</Field>
      <Field label={form.is_sparplan ? "Steuern (aus Tranchen)" : "Steuer (€, negativ = Erstattung)"}>{inp("tax", { type: "number", step: "any", readOnly: form.is_sparplan })}</Field>
      <Field label="Broker"><select value={form.broker || ""} onChange={e => f("broker", e.target.value)}>
        <option value="">–</option>{BROKERS.map(b => <option key={b}>{b}</option>)}</select></Field>

      <label className="jf-check full">
        <input type="checkbox" checked={!!form.signal_source} onChange={e => f("signal_source", e.target.checked)} />
        <span>Nexus-Signal</span>
      </label>
      {form.signal_source && <>
        <Field label="Strategien (bis zu 4)" full>
          <StrategiesInput strategies={form.sigmund_strategies || [""]} onChange={s => f("sigmund_strategies", s)} />
        </Field>
        <Field label="Signal-Score">{inp("sigmund_score", { type: "number", step: "any" })}</Field>
      </>}
      <Field label="Notizen" full>
        <textarea value={form.notes || ""} onChange={e => f("notes", e.target.value)} placeholder="Setup, Begründung …" rows={3} />
      </Field>
    </div>
  );
}
